import { useState } from "react";
import { useAccess } from "../../../../access/hooks/useAccess";
import { ludDashboardPermissions } from "../../../../access/modules/lud-dashboard";
import { Button } from "../../../ui";
import { isBranchAction } from "../../../../utils/branchWorkflow";
import BranchActionDialog, { BRANCH_EMAIL_PURPOSE } from "./BranchActionDialog";
import CancelRequestDialog from "./CancelRequestDialog";
import SendEmailDialog from "./SendEmailDialog";

// Quick actions of an in-progress working-hours / maintenance request, on its row.
// What is offered follows the request's stage (from the list) and the viewer's
// role; the server re-checks both on every call. Anything that needs the
// per-application picture (confirming one application, overrides, retries)
// opens the details instead of guessing.
const SEND_STAGES = ["approved", "partiallySent", "sendBlocked"];
const CONFIRM_STAGES = ["awaitingConfirmation", "partiallyConfirmed"];

export function branchStage(item) {
  if (!item.actions?.branchWorkflow || !isBranchAction(item.kind?.code)) return null;
  return item.status?.lifecycle || (item.status?.native === "pending" ? "submitted" : null);
}

export default function BranchQuickActions({ item, t, lang, onDone, onOpenDetails, empty = null }) {
  const { has } = useAccess();
  const [dialog, setDialog] = useState(null);
  const stage = branchStage(item);
  const b = t.branch;
  const close = () => setDialog(null);
  const done = async () => {
    close();
    await onDone?.();
  };
  const buttons = [];
  const reviewer = Boolean(stage) && has(ludDashboardPermissions.branchWorkflow.review);
  if (reviewer && stage === "submitted") {
    buttons.push({ key: "approve", label: b.approve, variant: "success", appearance: "filled", dialog: "approve" });
    buttons.push({ key: "reject", label: b.reject, variant: "danger", appearance: "soft", dialog: "reject" });
  }
  if (reviewer && SEND_STAGES.includes(stage)) buttons.push({ key: "send", label: t.email.send, variant: "primary", appearance: "filled", dialog: "send" });
  if (reviewer && CONFIRM_STAGES.includes(stage)) buttons.push({ key: "confirm", label: b.confirm, variant: "success", appearance: "soft", details: true });
  if (reviewer && stage === "readyToFinalize") buttons.push({ key: "finalize", label: b.finalize, variant: "success", appearance: "filled", dialog: "finalize" });
  // Cancel while the server still allows it (pending, owner or administrator) - also for the cashier who raised it
  if (item.actions?.branchWorkflow && item.actions?.cancel) buttons.push({ key: "cancel", label: t.row.cancel, variant: "danger", appearance: "soft", dialog: "cancel" });
  if (!buttons.length) return empty;

  return (
    <>
      {buttons.map((button) => (
        <Button key={button.key} size="sm" variant={button.variant} appearance={button.appearance} onClick={() => (button.details ? onOpenDetails(item) : setDialog(button.dialog))}>
          {button.label}
        </Button>
      ))}
      {dialog === "cancel" && <CancelRequestDialog requestId={item.identity.id} t={t} onClose={close} onDone={done} />}
      {["approve", "reject", "finalize"].includes(dialog) && <BranchActionDialog type={dialog} requestId={item.identity.id} reference={item.reference} t={t} onClose={close} onDone={done} />}
      {dialog === "send" && <SendEmailDialog request={item} purpose={BRANCH_EMAIL_PURPOSE} t={t} lang={lang} onClose={close} onChanged={onDone} />}
    </>
  );
}
